import React from 'react';
import { Input, Button } from 'antd';
import { formatCurrencyWithConversion } from "../../../../utils/currencyUtils";

// Helper to parse numbers that may contain commas, dollar signs, or other formatting
const defaultParseNumber = (value) => {
  if (value == null || value === undefined || value === "") return 0;
  const cleanedValue = String(value)
    .replace(/[$€£¥₹,\s]/g, "")
    .trim();
  const parsed = parseFloat(cleanedValue);
  return isNaN(parsed) ? 0 : parsed;
};

// Column definitions for overpayment details table with allocation functionality
const getOverpaymentColumns = ({
  handleOverpaymentChange,
  handleAllocateOverpayment,
  allocations,
  loadingAllocation,
  remainingAmount,
  parseNumberFn = defaultParseNumber,
  onViewPartner,
}) => [
  {
    title: "Partner",
    dataIndex: "partner_name",
    key: "partner_name",
    width: 150,
    ellipsis: true,
    render: (text, record) => {
      // Get partner name from various possible fields
      const name = text || record?.partner?.name || "-";
      // Get partner ID from various possible fields
      const partnerId = record?.partner_id || record?.partner?.id || (typeof record?.partner === "number" ? record?.partner : null);
      if (partnerId && onViewPartner) {
        return (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onViewPartner(partnerId);
            }}
            style={{ color: "#1890ff", cursor: "pointer" }}
            title="View partner transactions"
          >
            {name}
          </a>
        );
      }
      return name;
    },
  },
  {
    title: "Transaction",
    dataIndex: "transaction_reference",
    key: "transaction_reference",
    width: 200,
    ellipsis: true,
    render: (text) => text || "N/A",
  },
  {
    title: "Date",
    dataIndex: "transaction_date",
    key: "transaction_date",
    width: 100,
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    width: 120,
    render: (amount, record) => {
      const currency = record?.currency || record?.originalData?.currency || "$";
      return formatCurrencyWithConversion(amount || 0, currency);
    },
  },
  {
    title: "Remaining",
    dataIndex: "remaining_amount",
    key: "remaining_amount",
    width: 120,
    render: (amount, record) => {
      const currency = record?.currency || record?.originalData?.currency || "$";
      return formatCurrencyWithConversion(amount || 0, currency);
    },
  },
  {
    title: "Allocate Amt",
    dataIndex: "overpaymentAmount",
    key: "overpaymentAmount",
    width: 130,
    render: (text, record) => {
      return (
        <Input
          type="text"
          size="small"
          value={text === 0 ? "" : text}
          onChange={(e) => {
            handleOverpaymentChange(record.id, e);
          }}
          disabled={loadingAllocation}
          style={{ width: 110 }}
          placeholder="Enter amount"
        />
      );
    },
  },
  {
    title: "Actions",
    key: "actions",
    width: 100,
    fixed: "right",
    render: (_, record) => {
      // Check if this overpayment has an existing allocation
      const existingAllocation = allocations && Array.isArray(allocations) ? allocations.find(
        (alloc) => alloc.target_overpayment_id === record.id
      ) : null;
      
      // Get the allocation amount if it exists (handle commas)
      const allocationAmount = existingAllocation 
        ? parseNumberFn(existingAllocation.amount || 0) 
        : 0;
      
      // Get the current input value (may be a string with commas)
      const currentAmount = record.overpaymentAmount;
      
      // Convert to number for comparison (handle commas)
      const numericAmount = parseNumberFn(currentAmount);
      
      // Button text based on action type
      let buttonText = "Allocate";
      let buttonType = "primary";

      if (existingAllocation) {
        if (numericAmount > 0 && numericAmount !== allocationAmount) {
          buttonText = "Update";
          buttonType = "primary";
        } else {
          buttonText = "Unallocate";
          buttonType = "danger";
        }
      }

      // Determine if this is an allocation or deallocation action
      const isAllocation =
        !existingAllocation ||
        (numericAmount > 0 && numericAmount !== allocationAmount);
      const isDeallocation = existingAllocation && buttonText === "Unallocate";
      
      // Use absolute values for comparison (handles both debit and credit overpayments)
      const sourceRemaining = Math.abs(parseNumberFn(remainingAmount || 0));
      const targetRemaining = Math.abs(
        parseNumberFn(record.remaining_amount || 0)
      );

      // Determine if button should be disabled
      const isDisabled =
        loadingAllocation ||
        (isAllocation && (!numericAmount || numericAmount <= 0)) ||
        (isAllocation &&
          numericAmount > Math.min(targetRemaining, sourceRemaining));

      return (
        <Button
          type={buttonType}
          size="small"
          onClick={() => {
            handleAllocateOverpayment(
              record.id, 
              isDeallocation ? 0 : numericAmount,
              existingAllocation ? existingAllocation.id : null
            );
          }}
          disabled={isDisabled}
          loading={loadingAllocation}
        >
          {buttonText}
        </Button>
      );
    },
  },
];

export default getOverpaymentColumns;
